import React, { ReactNode, createContext, useContext, useState } from 'react';

/** Navigation/UI section keys — not sent to the backend. */
export type NavKey = 'domains' | 'main';
/** Actual data domain keys that map to backend domain IDs. */
export type DomainKey = 'common' | 'location' | 'organization' | 'contact' | 'carbon';

const VALID_DOMAIN_KEYS: ReadonlySet<string> = new Set<DomainKey>([
  'common', 'location', 'organization', 'contact', 'carbon',
]);
export const isDomainKey = (v: string): v is DomainKey => VALID_DOMAIN_KEYS.has(v);

/** Full hierarchical data selection (cascade path + selected columns). */
export interface HierarchicalSelection {
  /** Path of selected values at each cascade level: [level0, level1, ..., levelN] */
  path: string[];
  /** Selected column/field IDs at the final level */
  selectedColumns: string[];
}

export interface DataSelectionContextType {
  /** Legacy: selectedFields for backward compatibility with old wheel-based UI. */
  selectedFields: Map<string, DomainKey>;
  /** New: full hierarchical selection from cascading dropdowns. */
  selection: HierarchicalSelection | null;
  /** Save legacy field selection. */
  saveFields: (fields: Map<string, DomainKey>) => void;
  /** Save new hierarchical selection. */
  saveSelection: (selection: HierarchicalSelection) => void;
  /** Clear all selections. */
  clearFields: () => void;
}

const DataSelectionContext = createContext<DataSelectionContextType | undefined>(undefined);

const STORAGE_KEY = 'iss.dataSelection.fields';
const STORAGE_KEY_SELECTION = 'iss.dataSelection.hierarchicalSelection';

const readStoredFields = (): Map<string, DomainKey> => {
  if (typeof window === 'undefined') {
    return new Map();
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return new Map();
    }

    const parsed: unknown = JSON.parse(raw);
    if (
      !Array.isArray(parsed) ||
      !parsed.every(
        (entry) =>
          Array.isArray(entry) &&
          entry.length === 2 &&
          typeof entry[0] === 'string' &&
          typeof entry[1] === 'string' &&
          isDomainKey(entry[1]),
      )
    ) {
      return new Map();
    }

    return new Map(parsed as Array<[string, DomainKey]>);
  } catch {
    return new Map();
  }
};

const writeStoredFields = (fields: Map<string, DomainKey>) => {
  if (typeof window === 'undefined') {
    return;
  }

  const entries = Array.from(fields.entries());
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
};

const readStoredSelection = (): HierarchicalSelection | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY_SELECTION);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !('path' in parsed) ||
      !('selectedColumns' in parsed) ||
      !Array.isArray((parsed as any).path) ||
      !Array.isArray((parsed as any).selectedColumns)
    ) {
      return null;
    }

    return parsed as HierarchicalSelection;
  } catch {
    return null;
  }
};

const writeStoredSelection = (selection: HierarchicalSelection) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(STORAGE_KEY_SELECTION, JSON.stringify(selection));
};

export const DataSelectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedFields, setSelectedFields] = useState<Map<string, DomainKey>>(() => readStoredFields());
  const [selection, setSelection] = useState<HierarchicalSelection | null>(() => readStoredSelection());

  const saveFields = (fields: Map<string, DomainKey>) => {
    const next = new Map(fields);
    setSelectedFields(next);
    writeStoredFields(next);
  };

  const saveSelection = (sel: HierarchicalSelection) => {
    setSelection(sel);
    writeStoredSelection(sel);
  };

  const clearFields = () => {
    setSelectedFields(new Map());
    setSelection(null);
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(STORAGE_KEY);
      window.sessionStorage.removeItem(STORAGE_KEY_SELECTION);
    }
  };

  return (
    <DataSelectionContext.Provider value={{ selectedFields, saveFields, clearFields, selection, saveSelection }}>
      {children}
    </DataSelectionContext.Provider>
  );
};

export const useDataSelection = (): DataSelectionContextType => {
  const context = useContext(DataSelectionContext);
  if (!context) {
    throw new Error('useDataSelection must be used within DataSelectionProvider');
  }
  return context;
};
